import React, { useState, useEffect, useCallback } from "react";
import {
    Container, Row, Col, Card, Button, Form, Spinner, Alert, Badge
} from "@themesberg/react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faLightbulb, faCheckCircle, faTimesCircle, faCalendarDay,
    faCalendarWeek, faCalendarAlt, faFilter, faSyncAlt,
    faShieldAlt, faChartPie, faUser, faUsers
} from "@fortawesome/free-solid-svg-icons";
import { useSelector } from "react-redux";
import { Redirect } from "react-router-dom";
import { Routes } from "../../routes";
import api from "../../api/api";

/* Helper: unwrap backend envelope {data, message, status} */
const unwrap = (res) => (res.data && res.data.data !== undefined ? res.data.data : res.data);

/* ─────────────────────────────────────────────────────────────
   Stat Card component
───────────────────────────────────────────────────────────────*/
const StatCard = ({ icon, label, value, color, loading }) => (
    <Card className="shadow-sm border-0 h-100" style={{ borderLeft: "4px solid", borderLeftColor: color }}>
        <Card.Body className="d-flex align-items-center gap-3">
            <div
                className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                style={{ width: 52, height: 52, background: color, opacity: 0.9 }}
            >
                <FontAwesomeIcon icon={icon} size="lg" className="text-white" />
            </div>
            <div>
                <div className="text-muted small fw-semibold text-uppercase" style={{ fontSize: "0.7rem", letterSpacing: "0.08em" }}>
                    {label}
                </div>
                <div className="fw-bold fs-4 text-dark">
                    {loading ? <Spinner animation="border" size="sm" /> : (value ?? "—")}
                </div>
            </div>
        </Card.Body>
    </Card>
);

/* ─────────────────────────────────────────────────────────────
   Main Page
───────────────────────────────────────────────────────────────*/
export default function CiemStatsDashboardPage() {
    const { roles } = useSelector((state) => state.auth);
    const isAdmin = roles && roles.includes("ROLE_ADMIN");

    // ── All hooks BEFORE any conditional return ───────────────
    const [stats, setStats] = useState(null);
    const [activeCount, setActiveCount] = useState(null);
    const [inactiveCount, setInactiveCount] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filterLoading, setFilterLoading] = useState(false);
    const [error, setError] = useState(null);
    const [filtersApplied, setFiltersApplied] = useState(false);
    const [filterState, setFilterState] = useState({
        status: "",
        startDate: "",
        endDate: "",
        userId: "",
        sectorExterno: "",
    });

    // ── Count by user state ───────────────────────────────────
    const [userIdQuery, setUserIdQuery] = useState("");
    const [userCount, setUserCount] = useState(null);
    const [userCountLoading, setUserCountLoading] = useState(false);
    const [userCountError, setUserCountError] = useState(null);
    const [userCountQueried, setUserCountQueried] = useState(false);

    // ── All users with ciem count (paginated) ─────────────────
    const [allUsersCiemStats, setAllUsersCiemStats] = useState([]);
    const [allUsersCiemLoading, setAllUsersCiemLoading] = useState(true);
    const [allUsersCiemPage, setAllUsersCiemPage] = useState(0);
    const [allUsersCiemSize, setAllUsersCiemSize] = useState(5);
    const [allUsersCiemTotal, setAllUsersCiemTotal] = useState({ pages: 0, elements: 0, first: true, last: true });
    const [allUsersCiemFilters, setAllUsersCiemFilters] = useState({ username: "", codeExpert: "" });
    const [allUsersCiemSearchInput, setAllUsersCiemSearchInput] = useState({ username: "", codeExpert: "" });

    // ── Fetch general stats ───────────────────────────────────
    const fetchGeneralStats = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [generalRes, activeRes, inactiveRes] = await Promise.all([
                api.get("/api/dashboard/ciems/stats"),
                api.get("/api/dashboard/ciems/count/by-status?status=true"),
                api.get("/api/dashboard/ciems/count/by-status?status=false"),
            ]);
            setStats(unwrap(generalRes));
            setActiveCount(unwrap(activeRes));
            setInactiveCount(unwrap(inactiveRes));
            setFiltersApplied(false);
        } catch (err) {
            setError("Error al cargar las estadísticas. Verifique la conexión con el servidor.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (isAdmin) fetchGeneralStats();
    }, [fetchGeneralStats, isAdmin]);

    // ── Fetch paginated all-users ciem count ─────────────────────
    const fetchAllUsersCiem = useCallback(async (page, size, filters) => {
        setAllUsersCiemLoading(true);
        try {
            const params = new URLSearchParams({ page, size });
            if (filters.username.trim()) params.append("username", filters.username.trim());
            if (filters.codeExpert.trim()) params.append("codeExpert", filters.codeExpert.trim());
            const res = await api.get(`/api/dashboard/ciems/count/by-all-users?${params.toString()}`);
            const raw = unwrap(res);
            // Support both Spring Page envelope and plain array
            if (raw && Array.isArray(raw.content)) {
                setAllUsersCiemStats(raw.content);
                setAllUsersCiemTotal({
                    pages: raw.totalPages ?? 1,
                    elements: raw.totalElements ?? raw.content.length,
                    first: raw.first ?? page === 0,
                    last: raw.last ?? true,
                });
            } else if (Array.isArray(raw)) {
                setAllUsersCiemStats(raw);
                setAllUsersCiemTotal({ pages: 1, elements: raw.length, first: true, last: true });
            } else {
                setAllUsersCiemStats([]);
            }
        } catch {
            setAllUsersCiemStats([]);
        } finally {
            setAllUsersCiemLoading(false);
        }
    }, []);

    useEffect(() => {
        if (isAdmin) fetchAllUsersCiem(allUsersCiemPage, allUsersCiemSize, allUsersCiemFilters);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchAllUsersCiem, isAdmin, allUsersCiemPage, allUsersCiemSize, allUsersCiemFilters]);

    // ── Role guard – after all hooks ──────────────────────────
    if (!isAdmin) {
        return <Redirect to={Routes.NotFound.path} />;
    }

    // ── Handlers ──────────────────────────────────────────────
    const handleApplyFilters = async (e) => {
        e.preventDefault();
        setFilterLoading(true);
        setError(null);
        try {
            const payload = {};
            if (filterState.status !== "") payload.status = filterState.status === "true";
            if (filterState.startDate) payload.startDate = new Date(filterState.startDate).toISOString();
            if (filterState.endDate) payload.endDate = new Date(filterState.endDate).toISOString();
            if (filterState.userId !== "") payload.userId = parseInt(filterState.userId, 10);
            if (filterState.sectorExterno.trim()) payload.sectorExterno = filterState.sectorExterno.trim();

            const res = await api.post("/api/dashboard/ciems/stats/filter", payload);
            setStats(unwrap(res));
            setFiltersApplied(true);
        } catch (err) {
            setError("Error al aplicar los filtros. Intente nuevamente.");
        } finally {
            setFilterLoading(false);
        }
    };

    const handleClearFilters = () => {
        setFilterState({ status: "", startDate: "", endDate: "", userId: "", sectorExterno: "" });
        fetchGeneralStats();
    };

    const handleFilterChange = (field, value) => {
        setFilterState((prev) => ({ ...prev, [field]: value }));
    };

    const handleCountByUser = async (e) => {
        e.preventDefault();
        if (!userIdQuery.toString().trim()) {
            setUserCountError("Debes ingresar un ID de usuario.");
            return;
        }
        setUserCountLoading(true);
        setUserCountError(null);
        setUserCount(null);
        setUserCountQueried(false);
        try {
            const res = await api.get(`/api/dashboard/ciems/count/by-user?userId=${encodeURIComponent(userIdQuery)}`);
            setUserCount(unwrap(res));
            setUserCountQueried(true);
        } catch (err) {
            setUserCountError("Error al consultar por usuario. Intente nuevamente.");
        } finally {
            setUserCountLoading(false);
        }
    };

    const handleClearUserCount = () => {
        setUserIdQuery("");
        setUserCount(null);
        setUserCountError(null);
        setUserCountQueried(false);
    };

    // ── Cards config ──────────────────────────────────────────
    const cards = [
        { icon: faLightbulb, label: "Total Ideas", value: stats?.totalCiems, color: "#0d6efd" },
        { icon: faCheckCircle, label: "Ideas Activas", value: stats?.activeCiems, color: "#198754" },
        { icon: faTimesCircle, label: "Ideas Inactivas", value: stats?.inactiveCiems, color: "#dc3545" },
        { icon: faCalendarDay, label: "Creadas Hoy", value: stats?.ciemsCreatedToday, color: "#fd7e14" },
        { icon: faCalendarWeek, label: "Creadas esta Semana", value: stats?.ciemsCreatedThisWeek, color: "#6f42c1" },
        { icon: faCalendarAlt, label: "Creadas este Mes", value: stats?.ciemsCreatedThisMonth, color: "#0dcaf0" },
        { icon: faChartPie, label: "Creadas este Año", value: stats?.ciemsCreatedThisYear, color: "#20c997" },
    ];

    // ── Render ────────────────────────────────────────────────
    return (
        <Container className="py-4">

            {/* Header */}
            <div className="d-flex align-items-center justify-content-between mb-4">
                <div>
                    <h3 className="fw-bold mb-0 d-flex align-items-center gap-2">
                        <FontAwesomeIcon icon={faShieldAlt} className="text-primary" />
                        Dashboard de Ideas de Negocio
                    </h3>
                    <p className="text-muted mb-0 small mt-1">Estadísticas y métricas de CIEMs del sistema</p>
                </div>
                <div className="d-flex align-items-center gap-2">
                    {filtersApplied && (
                        <Badge bg="warning" text="dark" className="px-3 py-2">Filtros aplicados</Badge>
                    )}
                    <Button variant="outline-primary" size="sm" onClick={handleClearFilters} disabled={loading}>
                        <FontAwesomeIcon icon={faSyncAlt} className={`me-2 ${loading ? "fa-spin" : ""}`} />
                        {filtersApplied ? "Restablecer" : "Actualizar"}
                    </Button>
                </div>
            </div>

            {/* Error */}
            {error && (
                <Alert variant="danger" className="mb-4" onClose={() => setError(null)} dismissible>
                    {error}
                </Alert>
            )}

            {/* Stat Cards */}
            <Row className="g-3 mb-4">
                {cards.map((c) => (
                    <Col key={c.label} xs={12} sm={6} md={4} lg={3}>
                        <StatCard icon={c.icon} label={c.label} value={c.value} color={c.color} loading={loading || filterLoading} />
                    </Col>
                ))}
            </Row>

            {/* Count by Status – real-time (only when no filter applied) */}
            {!filtersApplied && (
                <Row className="g-3 mb-4">
                    <Col xs={12} sm={6}>
                        <Card className="border-0 shadow-sm h-100" style={{ background: "linear-gradient(135deg, #d1e7dd 0%, #a3cfbb 100%)" }}>
                            <Card.Body className="text-center py-4">
                                <FontAwesomeIcon icon={faCheckCircle} size="2x" className="text-success mb-2" />
                                <h2 className="fw-bold text-success mb-0">
                                    {loading ? <Spinner animation="border" size="sm" /> : (activeCount ?? "—")}
                                </h2>
                                <div className="text-muted small fw-semibold mt-1">Activas (en vivo)</div>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col xs={12} sm={6}>
                        <Card className="border-0 shadow-sm h-100" style={{ background: "linear-gradient(135deg, #f8d7da 0%, #f1aeb5 100%)" }}>
                            <Card.Body className="text-center py-4">
                                <FontAwesomeIcon icon={faTimesCircle} size="2x" className="text-danger mb-2" />
                                <h2 className="fw-bold text-danger mb-0">
                                    {loading ? <Spinner animation="border" size="sm" /> : (inactiveCount ?? "—")}
                                </h2>
                                <div className="text-muted small fw-semibold mt-1">Inactivas (en vivo)</div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}

            {/* Count by User */}
            <Card className="shadow-sm border-0 mb-4">
                <Card.Header className="bg-white border-bottom d-flex align-items-center gap-2 py-3">
                    <FontAwesomeIcon icon={faUser} className="text-primary" />
                    <span className="fw-semibold">Ideas de negocio por usuario</span>
                </Card.Header>
                <Card.Body>
                    {userCountError && (
                        <Alert variant="danger" className="mb-3" onClose={() => setUserCountError(null)} dismissible>
                            {userCountError}
                        </Alert>
                    )}
                    <Form onSubmit={handleCountByUser}>
                        <Row className="g-3 align-items-end">
                            <Col xs={12} sm={6} md={5}>
                                <Form.Group controlId="cuUserId">
                                    <Form.Label className="small fw-semibold text-muted">ID de usuario</Form.Label>
                                    <Form.Control
                                        type="number"
                                        min="1"
                                        placeholder="Ej: 42"
                                        value={userIdQuery}
                                        onChange={(e) => setUserIdQuery(e.target.value)}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                            <Col xs={12} sm={6} md={7} className="d-flex gap-2">
                                <Button variant="primary" type="submit" disabled={userCountLoading} style={{ minWidth: 140 }}>
                                    {userCountLoading ? (
                                        <><Spinner animation="border" size="sm" className="me-2" />Consultando...</>
                                    ) : (
                                        <><FontAwesomeIcon icon={faUser} className="me-2" />Consultar</>
                                    )}
                                </Button>
                                {userCountQueried && (
                                    <Button variant="outline-secondary" type="button" onClick={handleClearUserCount}>
                                        Limpiar
                                    </Button>
                                )}
                            </Col>
                        </Row>
                    </Form>

                    {userCountQueried && !userCountLoading && (
                        <div className="mt-4 p-4 rounded-3 text-center" style={{ background: "linear-gradient(135deg, #e0f0ff 0%, #cce5ff 100%)" }}>
                            <FontAwesomeIcon icon={faLightbulb} size="2x" className="text-primary mb-2" />
                            <div className="display-5 fw-bold text-primary">{userCount ?? 0}</div>
                            <div className="text-muted small mt-1">
                                ideas de negocio del usuario con ID <strong>{userIdQuery}</strong>
                            </div>
                        </div>
                    )}
                </Card.Body>
            </Card>

            {/* ── All users with ciem count (paginated) ────────────── */}
            <Card className="shadow-sm border-0 mb-4">
                <Card.Header className="bg-white border-bottom py-3">
                    <div className="d-flex align-items-center gap-2 mb-3">
                        <FontAwesomeIcon icon={faUsers} className="text-warning" />
                        <span className="fw-semibold">Ideas de negocio por usuario</span>
                    </div>
                    {/* Search filters */}
                    <Row className="g-2">
                        <Col xs={12} sm={5}>
                            <Form.Control
                                type="text"
                                size="sm"
                                placeholder="Buscar por usuario"
                                value={allUsersCiemSearchInput.username}
                                onChange={(e) => setAllUsersCiemSearchInput((p) => ({ ...p, username: e.target.value }))}
                            />
                        </Col>
                        <Col xs={12} sm={5}>
                            <Form.Control
                                type="text"
                                size="sm"
                                placeholder="Buscar por código experto"
                                value={allUsersCiemSearchInput.codeExpert}
                                onChange={(e) => setAllUsersCiemSearchInput((p) => ({ ...p, codeExpert: e.target.value }))}
                            />
                        </Col>
                        <Col xs={6} sm={1}>
                            <Form.Select
                                size="sm"
                                value={allUsersCiemSize}
                                onChange={(e) => { setAllUsersCiemSize(Number(e.target.value)); setAllUsersCiemPage(0); }}
                            >
                                {[5, 10, 20, 50].map((n) => <option key={n} value={n}>{n}</option>)}
                            </Form.Select>
                        </Col>
                        <Col xs={6} sm={1}>
                            <Button
                                size="sm"
                                variant="primary"
                                className="w-100"
                                onClick={() => {
                                    setAllUsersCiemPage(0);
                                    setAllUsersCiemFilters({ ...allUsersCiemSearchInput });
                                }}
                            >
                                Buscar
                            </Button>
                        </Col>
                    </Row>
                </Card.Header>
                <Card.Body className="p-0">
                    {allUsersCiemLoading ? (
                        <div className="text-center py-4"><Spinner animation="border" variant="warning" /></div>
                    ) : allUsersCiemStats.length === 0 ? (
                        <div className="text-center text-muted py-4">Sin datos disponibles.</div>
                    ) : (
                        <>
                            <div className="table-responsive">
                                <table className="table table-hover mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th className="ps-4">ID</th>
                                            <th>Usuario</th>
                                            <th>Nombre completo</th>
                                            <th className="text-end pe-4">Ideas</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {allUsersCiemStats.map((u) => (
                                            <tr key={u.userId}>
                                                <td className="ps-4 text-muted small">{u.userId}</td>
                                                <td className="fw-semibold">{u.username ?? "—"}</td>
                                                <td className="text-muted">
                                                    {[u.firstName, u.lastName].filter(Boolean).join(" ") || "—"}
                                                </td>
                                                <td className="text-end pe-4">
                                                    <span
                                                        className="badge rounded-pill"
                                                        style={{
                                                            background: u.ciemCount > 0 ? "#fd7e14" : "#6c757d",
                                                            color: "#fff",
                                                            minWidth: 40,
                                                            fontSize: "0.85rem"
                                                        }}
                                                    >
                                                        {u.ciemCount ?? 0}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {/* Pagination */}
                            <div className="d-flex align-items-center justify-content-between px-3 py-2 border-top bg-light">
                                <small className="text-muted">
                                    Página {allUsersCiemPage + 1} de {allUsersCiemTotal.pages} &middot; {allUsersCiemTotal.elements} registros
                                </small>
                                <div className="d-flex gap-2">
                                    <Button
                                        size="sm"
                                        variant="outline-secondary"
                                        disabled={allUsersCiemTotal.first}
                                        onClick={() => setAllUsersCiemPage((p) => Math.max(0, p - 1))}
                                    >
                                        &laquo; Anterior
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline-secondary"
                                        disabled={allUsersCiemTotal.last}
                                        onClick={() => setAllUsersCiemPage((p) => p + 1)}
                                    >
                                        Siguiente &raquo;
                                    </Button>
                                </div>
                            </div>
                        </>
                    )}
                </Card.Body>
            </Card>

            {/* Filter Panel */}
            <Card className="shadow-sm border-0 mb-4">
                <Card.Header className="bg-white border-bottom d-flex align-items-center gap-2 py-3">
                    <FontAwesomeIcon icon={faFilter} className="text-secondary" />
                    <span className="fw-semibold">Filtrar estadísticas</span>
                </Card.Header>
                <Card.Body>
                    <Form onSubmit={handleApplyFilters}>
                        <Row className="g-3 align-items-end">

                            {/* Estado */}
                            <Col xs={12} sm={6} md={4} lg={2}>
                                <Form.Group controlId="fStatus">
                                    <Form.Label className="small fw-semibold text-muted">Estado</Form.Label>
                                    <Form.Select
                                        value={filterState.status}
                                        onChange={(e) => handleFilterChange("status", e.target.value)}
                                    >
                                        <option value="">Todos</option>
                                        <option value="true">Activos</option>
                                        <option value="false">Inactivos</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>

                            {/* Fecha inicio */}
                            <Col xs={12} sm={6} md={4} lg={3}>
                                <Form.Group controlId="fStartDate">
                                    <Form.Label className="small fw-semibold text-muted">Fecha inicio</Form.Label>
                                    <Form.Control
                                        type="datetime-local"
                                        value={filterState.startDate}
                                        onChange={(e) => handleFilterChange("startDate", e.target.value)}
                                    />
                                </Form.Group>
                            </Col>

                            {/* Fecha fin */}
                            <Col xs={12} sm={6} md={4} lg={3}>
                                <Form.Group controlId="fEndDate">
                                    <Form.Label className="small fw-semibold text-muted">Fecha fin</Form.Label>
                                    <Form.Control
                                        type="datetime-local"
                                        value={filterState.endDate}
                                        onChange={(e) => handleFilterChange("endDate", e.target.value)}
                                    />
                                </Form.Group>
                            </Col>

                            {/* User ID */}
                            <Col xs={12} sm={6} md={4} lg={2}>
                                <Form.Group controlId="fUserId">
                                    <Form.Label className="small fw-semibold text-muted">ID Usuario</Form.Label>
                                    <Form.Control
                                        type="number"
                                        min="1"
                                        placeholder="Ej: 42"
                                        value={filterState.userId}
                                        onChange={(e) => handleFilterChange("userId", e.target.value)}
                                    />
                                </Form.Group>
                            </Col>

                            {/* Sector externo */}
                            <Col xs={12} sm={6} md={4} lg={2}>
                                <Form.Group controlId="fSectorExterno">
                                    <Form.Label className="small fw-semibold text-muted">Sector externo</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Ej: Tech"
                                        value={filterState.sectorExterno}
                                        onChange={(e) => handleFilterChange("sectorExterno", e.target.value)}
                                    />
                                </Form.Group>
                            </Col>

                            {/* Buttons */}
                            <Col xs={12} className="d-flex gap-2 flex-wrap">
                                <Button variant="primary" type="submit" disabled={filterLoading} style={{ minWidth: 160 }}>
                                    {filterLoading ? (
                                        <><Spinner animation="border" size="sm" className="me-2" />Calculando...</>
                                    ) : (
                                        <><FontAwesomeIcon icon={faFilter} className="me-2" />Aplicar filtros</>
                                    )}
                                </Button>
                                <Button variant="outline-secondary" type="button" onClick={handleClearFilters} disabled={loading || filterLoading}>
                                    Limpiar
                                </Button>
                            </Col>

                        </Row>
                    </Form>
                </Card.Body>
            </Card>

        </Container>
    );
}
